<template lang="pug">
  .b-project-file-frame(
    :class="[selected ? 'selected' : '', ((fileIndex + 1) % 2 === 0) ? 'even' : 'odd', 'orientation-' + orientationType, device]"
    draggable="true"
    @dragstart="dragStart"
    @drag="drag"
  )
    BaseCheckbox._checkbox(:value="selected" @input="changeFileSelection($event, fileData)")
    ._preview-container(
      :style="{ backgroundColor: hasThumbnail ? 'transparent' : color }"
      :class="{ hasPreview, isBlank }"
      @click="clickFile"
    )
      ._preview-label(v-if="orientationType === 'square' && hasThumbnail"
        :style="{ backgroundColor: color }") {{extension.toUpperCase()}}
      img._preview-img(
        v-if="hasThumbnail"
        :src="fileData.file.thumbnails.preview"
        :draggable="false"
      )
      ._preview-format(v-else) {{extension.toUpperCase()}}
    ._file-name(:class="{'break-all' : noBreakableSymbols}" :title="nameWithoutExtension" v-html="nameDisplayed")
    ._file-size {{ storageSize.toUpperCase() }}
    ._user-avatar
      UserAvatar(:user="fileData.file.owner" :size="orientationType === 'square' ? 26 : 44")
    ._user-name
      | {{ userFullName.surname }}
      br(v-if="orientationType === 'rectangle'")
      |  {{ userFullName.name }}
    ._created {{ createdAt }}
    ._menu
      EllipsisMenu(:items="menuItems" :vertical="orientationType === 'square'")
</template>

<script>
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import UserAvatar from '@/components/UserAvatar.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import utils from '@/utils/common';
import fileTypes from '@/utils/fileTypes';

export default {
  name: 'ProjectFileFrame',
  props: {
    fileData: {
      type: Object,
      default: () => ({}),
    },
    fileIndex: {
      type: Number,
      default: 0,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    orientationType: {
      type: String,
      default: 'square',
    },
  },
  data() {
    return {
      menuItems: [
        { text: 'Поделиться', icon: 'share', handler: this.share },
        { text: 'Переместить', icon: 'arrows-crossed', handler: () => { this.$emit('clickMenu', 'move', this.fileData); } },
        { text: 'Скачать', icon: 'arrow-download', handler: () => { this.$emit('clickMenu', 'download', this.fileData); } },
        { text: 'Удалить', icon: 'archive', handler: () => { this.$emit('clickMenu', 'delete', this.fileData); } },
      ],
      device: 'desktop',
    };
  },
  components: { BaseCheckbox, UserAvatar, EllipsisMenu },
  mounted() {
    window.addEventListener('resize', this.checkResize);
    this.checkResize();
  },
  beforeDestroy() {
    document.removeEventListener('resize', this.checkResize);
  },
  computed: {
    extension() { return this.fileData.file.extension.toUpperCase(); },
    fileInfo() { return fileTypes[this.extension.toLowerCase()] ?? []; },
    hasThumbnail() { return this.fileInfo.hasThumbnail; },
    hasPreview() { return this.fileInfo.hasPreview; },
    isBlank() { return this.fileInfo.blank; },
    color() { return this.fileInfo.color || '#788CA1'; },
    storageSize() { return utils.storageSizeCounter(this.fileData?.file?.size); },
    userFullName() { return { surname: this.fileData.file?.owner.surname, name: this.fileData.file?.owner.name }; },
    createdAt() { return this.fileData.file.createdAt && utils.convertDateToString(new Date(this.fileData.file.createdAt), 'short'); },
    nameWithoutExtension() { return this.fileData.file.name.replace(/\.[^/.]+$/, ''); },
    nameDisplayed() {
      const breakableSymbols = [' ', '-', '.', '_'];
      const maxLength = 60;
      const isMoreThanMax = this.nameWithoutExtension.length > maxLength;
      const cutString = !isMoreThanMax ? this.nameWithoutExtension : this.nameWithoutExtension.slice(0, maxLength - 3);
      let result = '';
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < cutString.length; i++) {
        if (breakableSymbols.includes(cutString[i])) {
          result += `${cutString[i]}<wbr>`;
        } else result += cutString[i];
      }
      return isMoreThanMax ? `${result}...` : result;
    },
    noBreakableSymbols() {
      const breakableSymbols = [' ', '-', '.', '_'];
      return !Array.from(this.nameWithoutExtension).find(letter => breakableSymbols.includes(letter));
    },
  },
  methods: {
    share() {
      navigator.clipboard.writeText(`${document.location.origin}${this.fileData.file.url}`);
      this.$push({ text: 'Ссылка скопирована в буфер обмена' });
    },
    clickFile() {
      if (this.hasPreview) {
        this.$emit('viewImage', this.fileData);
      } else if (this.isBlank) { // is it needed?
        window.open(window.location.origin + this.fileData.file.url, '_blank');
      }
    },
    changeFileSelection(event, fileData) {
      this.$emit('changeFileSelection', fileData.id);
    },
    checkResize() {
      if (window.innerWidth > 1200) this.device = 'desktop';
      else if (window.innerWidth < 768) this.device = 'mobile';
      else this.device = 'tablet';
    },
    dragStart(e) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setDragImage(e.target, window.outerWidth, window.outerHeight);
      this.$emit('fileDragStart', e, this.fileData);
    },
    drag(e) {
      this.$emit('fileDrag', e);
      return false;
    },
  },
};
</script>

<style scoped lang="scss">
  .project-file-frame {
    font-family: "Geometria", sans-serif;
    position: relative;
    box-sizing: border-box;
    width: 100%;
    height: 68px;
    display: flex;
    align-items: center;
    padding: 14px;
    border-radius: 8px;
    background-color: var(--main-bg);
    user-select: none;

    &.orientation-rectangle & {
      &__file-name {
        width: 35%;
      }

      &__file-size {
        width: 15%;
      }

      &__user-name {
        width: 25%;
      }

      &__created {
        width: 15%;
      }
    }

    &.mobile.orientation-square & {
      &__preview {
        &-container {
          margin: 78px 0 0;
        }
      }
    }

    &.tablet.orientation-square & {
      &__preview {
        &-container {
          margin: 62px 0 0;
        }
      }
    }

    &.orientation-square {
      min-width: 210px;
      min-height: 215px;
      flex-direction: column;
      padding: 0;

      &.tablet {
        min-width: 265px;
        min-height: 265px;
      }

      &.mobile {
        min-width: 288px;
        min-height: 288px;
      }
    }

    &.orientation-square & {
      &__preview {
        &-container {
          position: relative;
          margin: 48px 0 0;
          width: 82px;
          min-height: 82px;
          border-radius: 10px;
        }

        &-label {
          position: absolute;
          top: -10px;
          left: -2px;
          border-radius: 4px;
          padding: 2px 8px;
          font-family: "Geometria", sans-serif;
          font-style: normal;
          font-weight: 500;
          font-size: 12px;
          line-height: 15px;
        }

        &-img {
          height: 82px;
          width: 82px;
        }
      }

      &__file {
        &-name {
          width: 100%;
          padding: 0 10px;
          text-align: center;
          margin-top: 7px;
          margin-right: 0;
          max-width: 200px;
        }

        &-size {
          text-align: center;
        }
      }

      &__checkbox {
        position: absolute;
        top: 15px;
        left: 15px;
      }

      &__user {
        &-avatar {
          position: absolute;
          right: 15px;
          bottom: 9px;
          margin-right: 0;
        }

        &-name {
          width: auto;
          position: absolute;
          bottom: 21px;
          left: 15px;
          margin-right: 0;
        }
      }

      &__menu {
        position: absolute;
        top: 15px;
        right: 15px;
      }

      &__created {
        position: absolute;
        left: 15px;
        bottom: 7px;
      }
    }

    &.even:not(.selected):not(:hover) {
      background-color: var(--main-bg);
    }

    &.odd:not(.orientation-square):not(.selected):not(:hover) {
      background-color: var(--color-white);
    }

    &.selected {
      background-color: #EFF7FF;
    }

    &:hover {
      background-color: #EFF7FF;
    }

    &__preview {
      &-container {
        min-width: 40px;
        height: 40px;
        margin: 0 13px 0 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 8px;

        &.hasPreview, &.isBlank {
          cursor: pointer;
        }
      }

      &-format {
        color: white;
        font-style: normal;
        font-weight: 700;
        font-size: 14px;
        line-height: 18px;
      }

      &-img {
        width: 40px;
        height: 40px;
        object-fit: cover;
        border-radius: 8px;
      }
    }

    &__file {
      &-name {
        max-width: 330px;
        margin-right: 10px;
        color: var(--color-dark-gray);
        font-style: normal;
        font-weight: 500;
        font-size: 12px;
        line-height: 15px;
        max-height: 30px;
        overflow-y: hidden;

        &.break-all {
          word-break: break-all;
        }
      }

      &-size {
        max-width: 200px;
        color: var(--color-dark-gray);
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 15px;
      }
    }

    &__user {
      &-avatar {
        margin-right: 14px;
        cursor: pointer;
      }

      &-name {
        max-width: 350px;
        margin-right: 10px;
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 15px;
        color: var(--color-accent);
        cursor: pointer;
      }
    }

    &__created {
      max-width: 155px;
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      line-height: 15px;
      color: var(--color-dark-gray);
      margin-right: 10px;
    }
  }

</style>
